import { useMemo } from 'react';
import {
  useDatabaseRange,
  useFlattenedGeometryShapes,
  usePortsGetList,
} from '@ais-ui/api';
import { useGetFilters, useGetShareGroups } from '@ais-ui/websockets';
import type {
  ShapeCollection,
  PortCollection,
  FilterCollection,
} from '../../utils/formValueBuilders';
import type { ReportType } from '../../../reportsTypes';

interface ReportFormResources {
  shapes: ShapeCollection;
  filters: FilterCollection | undefined;
  ports: PortCollection;
  databaseRange: ReturnType<typeof useDatabaseRange>['data'];
  isLoading: boolean;
  isLoadingShareGroups: boolean;
  isLoadingDatabaseRange: boolean;
}

interface UseReportFormResourcesParams {
  reportType?: ReportType;
}

export function useReportFormResources(
  params?: UseReportFormResourcesParams
): ReportFormResources {
  const { reportType } = params || {};

  const { data: shapesResponse, isLoading: isLoadingShapes } =
    useFlattenedGeometryShapes();
  const { data: filtersResponse, isLoading: isLoadingFilters } =
    useGetFilters();
  const { data: portsResponse, isLoading: isLoadingPorts } = usePortsGetList();
  const { isLoading: isLoadingShareGroups } = useGetShareGroups();
  const { data: databaseRange, isLoading: isDatabaseRangeLoading } =
    useDatabaseRange();

  const shapes = useMemo<ShapeCollection>(() => {
    return shapesResponse ?? [];
  }, [shapesResponse]);

  const filters = useMemo<FilterCollection | undefined>(() => {
    if (!filtersResponse) return undefined;
    return filtersResponse;
  }, [filtersResponse]);

  const ports = useMemo<PortCollection>(() => {
    return portsResponse?.records ?? [];
  }, [portsResponse]);

  // Check if the current report type requires ports
  const requiresPorts = useMemo(() => {
    return reportType?.shape?.ports !== undefined;
  }, [reportType]);

  const isLoading = useMemo(() => {
    // Shapes and filters should always load successfully
    const criticalResourcesLoading = isLoadingShapes || isLoadingFilters;

    // Only include ports loading if the report type actually uses ports
    // Some customers don't have access to ports, so we shouldn't block on it
    // unless it's actually required (e.g., PORT_CALLS report)
    const portsLoading = requiresPorts ? isLoadingPorts : false;

    return criticalResourcesLoading || portsLoading;
    // Note: isDatabaseRangeLoading is intentionally excluded - this endpoint is not critical
    // and should not block the form from loading (it can return 503 errors)
  }, [isLoadingShapes, isLoadingFilters, isLoadingPorts, requiresPorts]);

  return {
    shapes,
    filters,
    ports,
    databaseRange,
    isLoading,
    isLoadingShareGroups,
    isLoadingDatabaseRange: isDatabaseRangeLoading,
  };
}
